import { effect, inject, Injectable, signal } from '@angular/core';
import { Toast } from '@mwe/models';
import { NavigationEnd, Router } from '@angular/router';
import { tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly router = inject(Router);

  private readonly currentToast = signal<Toast>(null);
  private readonly toastQueue = signal<Toast[]>([]);

  constructor() {
    this.router.events
      .pipe(
        tap(event => {
          if (event instanceof NavigationEnd) {
            if (!this.currentToast()?.staysVisible) return;
            this.currentToast.set(null);
          }
        }),
      )
      .subscribe();

    effect(() => {
      const currentToast = this.currentToast();
      const toastQueue = this.toastQueue();

      // only when the previous toast concluded and another toast is in queue
      if (!currentToast && toastQueue.length > 0) {
        const newToast = toastQueue.shift();
        this.publishNewToast({ newToast });
        this.toastQueue.set([...toastQueue]);
      }
    });
  }

  get toast() {
    return this.currentToast.asReadonly();
  }

  publishNewToast({ newToast, timeout = 5 * 1000 }: { newToast: Toast; timeout?: number }) {
    this.currentToast.set(newToast);

    if (!this.currentToast().staysVisible) {
      setTimeout(() => {
        this.currentToast.set(null);
      }, timeout);
    }
  }

  publishSuccess(message: string): void {
    const toastQueue = this.toastQueue();
    const newToast: Toast = { message, type: 'success', isClosable: true };
    this.toastQueue.set([...toastQueue, newToast]);
  }
}
